import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { getNotification } from '../../../../../components/Notification/helpers';

import {
  NOTIFICATION_TYPES,
  useNotification,
} from '../../../../../components/Notification';
import { sendCustomSentryBreadcrumb } from '../../../../../helpers/sentry';

import { MeasurementConfig } from '@cloudflare/speedtest';
import SpeedTest from '@cloudflare/speedtest';

import { getIsLowInternetSpeed } from '../../../../../helpers';
import { convertBitToMB } from '../../../../../modules/auth/components/ConnectionTest/helpers';
import { useMytaverseEvent } from '../../../../../modules/dashboard/providers';
import { IGamecastVideoStats } from '../../../../../modules/dashboard/components/DashboardContent/GameCast/interfaces';
import { StorageTypes } from '../../../../../interfaces/storage';
import { env } from '../../../../../env';

// run longer tests on desktop than on mobile
const MeasurementOptions: MeasurementConfig[] = [
  { type: 'download', bytes: 1e5, count: 1, bypassMinDuration: true },
  { type: 'download', bytes: 1e5, count: isMobile ? 9 : 30 },
  { type: 'download', bytes: 1e6, count: isMobile ? 2 : 4 },
];

const SpeedTestCheck = () => {
  const intervalRef = useRef<NodeJS.Timeout>();
  const { gamecastStats } = useMytaverseEvent();
  const { t: translate } = useTranslation('common');
  const { showNotification } = useNotification();

  const processSpeedTestResult = (message: string, results: any) => {
    const speed = convertBitToMB(results.getDownloadBandwidth() ?? 0);

    if (getIsLowInternetSpeed(speed)) {
      sendCustomSentryBreadcrumb({
        message: message + `. Browser connection speed is ${speed} Mpbs`,
      });
    }
  };

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const webrtcStats = gamecastStats.current as IGamecastVideoStats;

      if (
        webrtcStats &&
        webrtcStats.connectionSpeed >= 1 &&
        webrtcStats.connectionSpeed <
          +(env.REACT_APP_MIN_WEBRTC_SPEED_THRESHOLD_MBPS || 7)
      ) {
        const message = `GameCast stream connection speed (bitrate) is ${webrtcStats.connectionSpeed} Mbps`;

        const notificationData = getNotification({
          message: translate('notifications.unstableInternetTimer', {
            speed: env.REACT_APP_MIN_SPEED_THRESHOLD_MBPS || 15,
          }),
          type: NOTIFICATION_TYPES.WARNING,
        });

        showNotification({
          ...notificationData,
          logMessage: message,
          storageType: StorageTypes.Session,
        });

        const speedResult = new SpeedTest({
          measureUploadLoadedLatency: false,
          measureDownloadLoadedLatency: false,
          measurements: MeasurementOptions,
        });

        speedResult.onFinish = (results) => {
          if ((results as any).raw.download.finished) {
            processSpeedTestResult(message, results);
          }
        };

        speedResult.onResultsChange = () => {
          if ((speedResult.results as any).raw.download.finished) {
            processSpeedTestResult(message, speedResult.results);
          }
        };

        speedResult.onError = () => {
          sendCustomSentryBreadcrumb({
            message: message + '. Unable to run Cloudflare speed test.',
          });
        };
      }
    }, +(env.REACT_APP_SPEED_CHECK_TIMEOUT_MINS || 10) * 60000);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  return null;
};

export default SpeedTestCheck;
